import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useAlert } from "react-alert";
import QRCode from "qrcode";
import CareModal from "./CareModal";
import { getOrgOrder, updateOrgOrder, getAds } from "../../utility";
import LoadingSpinner from "../../components/spinners/DrinkSpinner";
import { emptyWholeCart } from "../../redux/cart/reducer";
import { useTranslation } from "react-i18next";
import updateProductQuantity from "../../utility/updateProductQuantity";

import styled from "styled-components";

const Button = styled.div`
  user-select: none;
  cursor: pointer;
  background: var(--gradient);
  border-radius: 15px;
  color: white;
  padding: 20px 30px;
  position: relative;
  width: 100%;
  height: 50px;
  margin-top: 9px;
  border: none;
  outline: none;
  box-shadow: none;
  font-size: 16px;
  font-weight: 700;
  font-family: "BR Frima Bold";
  height: 60px;
  text-transform: uppercase;
`;

const Progress = styled.div`
  margin: -20px -30px;
  background-color: pink;
  border-radius: 15px;
  height: 60px;
  position: absolute;
  width: 0;
  transition: width 0s;
  opacity: 0.5;
  &.start,
  &.done {
    width: 100%;
    transition: width 2s;
  }
`;

const Container = styled.div`
  text-align: center;
`;

function useLongPress(callback = () => {}, ms = 150) {
  const [startLongPress, setStartLongPress] = useState(false);

  useEffect(() => {
    let timerId;
    if (startLongPress) {
      timerId = setTimeout(callback, ms);
    } else {
      clearTimeout(timerId);
    }

    return () => {
      clearTimeout(timerId);
    };
  }, [startLongPress]);

  const start = useCallback(() => {
    setStartLongPress(true);
  }, []);
  const stop = useCallback(() => {
    setStartLongPress(false);
  }, []);

  return [
    startLongPress,
    {
      onMouseDown: start,
      onMouseUp: stop,
      onMouseLeave: stop,
      onTouchStart: start,
      onTouchEnd: stop,
    },
  ];
}

const ProgressButton = (props) => {
  const { t } = useTranslation("fastpass");
  const [startLongPress, backspaceLongPress] = useLongPress(props.longPressBackspaceCallback, 1500);
  let className = "progress";
  if (startLongPress) {
    className += " start";
  }

  return (
    <Button {...backspaceLongPress}>
      <Progress className={className}></Progress>
      <Container>{t("finishPress2s")}</Container>
    </Button>
  );
};

const CashlessSuccess = () => {
  const [careModalOpen, setCareModalOpen] = useState(false);
  const navigate = useNavigate();
  const { orgId, orderId } = useParams();
  const { t } = useTranslation("fastpass");

  const [order, setOrder] = useState();
  const [ad, setAd] = useState();
  const [qrTicket, setQrTicket] = useState();
  const alert = useAlert();
  const dispatch = useDispatch();

  const fetchAdvertisement = async () => {
    const fetchedAd = await getAds("bannerAd");
    setAd(fetchedAd.ad);
  };

  const fetchOrder = async () => {
    const savedOrder = await getOrgOrder(orgId, orderId);
    if (savedOrder) {
      setOrder(savedOrder.order);
    } else {
      alert.show(t("cannotLoadOrder"), { type: "error" });
    }
  };

  const handleConfirmation = async () => {
    let updatedOrder = { ...order, received: true };
    setOrder(updatedOrder);
    dispatch(emptyWholeCart({ orgId }));
    for (const product of updatedOrder.products) {
      if (product.stockLinks?.length || product.stockItemId || product.availableQuantity) {
        await updateProductQuantity(product, product.quantity, !!product.parentId, {
          orderId,
          venueId: product.uid,
        });
      }
    }
    try {
      await updateOrgOrder(orgId, orderId);
    } catch (error) {
      alert.show(t("cannotConfirmOrder"), { type: "error" });
    }
  };

  const setQrCodeHandler = async () => {
    try {
      const qrcode = await QRCode.toDataURL(orderId, {
        scale: 12,
      });
      setQrTicket(qrcode);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (orderId) {
      setQrCodeHandler();
      fetchOrder();
      fetchAdvertisement();
    } else {
      alert.show(t("cannotLoadOrder"), { type: "error" });
    }
  }, [orderId]);

  useEffect(() => {
    let intervalId = null;

    if (order && !order.received) {
      intervalId = setInterval(() => {
        fetchOrder().then((updatedOrder) => {
          if (updatedOrder.received) clearInterval(intervalId);
        });
      }, 600000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [order]);

  return (
    <>
      {order ? (
        <div className="d-flex flex-column" style={{ minHeight: "100vh" }}>
          <div className="page-header">
            <div className="container">
              <h2 className="title" style={{ color: order.received ? "#27AE60" : "white" }}>
                {order.received ? t("youAreServed") : t("orderNotReady")}
              </h2>
              <div>{order.received ? t("orderPending") : t("showAtBar")}</div>
            </div>
          </div>
          <div className="container px-20px py-4">
            <div className="summary-title">
              {`${t("orderSummary")}} : ${order.createdAt.toString().slice(-4)}`}
            </div>
            <div className="summary">
              <ul>
                {order.products.map((item, i) => (
                  <li key={i}>
                    <span>{item.title}</span>
                    <span className="count">x {item.quantity}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="mt-auto">
            <div className="page-header pt-3 pb-4">
              <div className="container">
                <div className="total--price mt-0 mb-12px">
                  <span>{t("total")}</span>
                  <span style={{ color: "#27AE60" }}>{order.totalAmount.toFixed(2)}€</span>
                </div>
                {order.received && ad && (
                  <div
                    className="text-center mb-12px"
                    onClick={() => window.open(ad.url, "_blank")}
                  >
                    <img src={ad.image} alt="promo" className="mw-100" />
                  </div>
                )}

                {order.received ? (
                  <button
                    type="button"
                    className="submitBtn submitBtn2 m-0"
                    style={{
                      background: "linear-gradient(91.24deg, #00C29F 0.26%, #009FC2 99.58%)",
                    }}
                    onClick={() => {
                      navigate(`/cashless/${orgId}`);
                    }}
                  >
                    {t("orderAgain")}
                  </button>
                ) : (
                  <div>
                    <div>
                      <div className="text-center">
                        <div className="mb-3 cashless--title">{t("showQr")}</div>
                        <img src={qrTicket} alt="" className="qrcode-ticket mx-auto" />
                      </div>
                      <div className="mb-12px">{t("pressOnlyWhenServed")}</div>
                      <ProgressButton longPressBackspaceCallback={() => handleConfirmation()} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "100vh", width: "100%" }}
        >
          <LoadingSpinner />
        </div>
      )}
      <CareModal open={careModalOpen} setOpen={setCareModalOpen} />
    </>
  );
};

export default CashlessSuccess;
